import React from "react";
import { COMPANY, TESTIMONIALS, PLANS, FAQS, TRUST_BADGES } from "../mock/data";
import { Button } from "./ui/button";
import { Phone, Star, ShieldCheck, Check, Award } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from "./ui/accordion";

export function BenefitsListSection() {
  const items = [
    { title: "Protect Sensitive Data", desc: "Keep your personal information, banking details, and private conversations safe from malware and spyware attacks." },
    { title: "Keep Your Phone Running Smoothly", desc: "Remove performance-killing malware and optimize your device for maximum speed and efficiency." },
    { title: "Avoid Storage Clutter", desc: "Automatically identify and remove unnecessary files, cache, and junk that slow down your device." },
    { title: "24/7 Protection Without Lifting a Finger", desc: "Set up once and enjoy continuous protection. No manual scans, no constant monitoring needed." }
  ];
  const stats = [
    { v: "99.9%", l: "Detection" },
    { v: "< 2%", l: "CPU Usage" },
    { v: "24/7", l: "Active Protection" },
    { v: "100%", l: "Privacy Safe" }
  ];
  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="section-title text-3xl md:text-4xl font-extrabold text-slate-900">Peace of Mind, Made Easy</h2>
          <p className="mt-3 text-slate-600">Experience the confidence that comes with knowing your Android device is protected around the clock.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 gap-5">
          {items.map((it) => (
            <div key={it.title} className="feature-card rounded-2xl border border-slate-200 bg-white p-6 flex gap-4">
              <div className="h-11 w-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 text-lg">{it.title}</h3>
                <p className="text-slate-600 text-sm mt-1 leading-relaxed">{it.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((s) => (
            <div key={s.l} className="rounded-2xl bg-emerald-50/70 border border-emerald-100 p-6 text-center">
              <p className="text-3xl font-extrabold text-emerald-700">{s.v}</p>
              <p className="text-xs uppercase tracking-widest text-slate-600 mt-1">{s.l}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <p className="text-slate-600">Join thousands of protected users. Call now to start your protection.</p>
          <Button asChild className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white h-12 px-6">
            <a href={`tel:${COMPANY.phoneRaw}`}>
              <Phone className="h-4 w-4 mr-2" /> Call {COMPANY.phone}
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
}

export function TestimonialsSection() {
  return (
    <section className="py-16 md:py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="section-title text-3xl md:text-4xl font-extrabold text-slate-900">Loved by Security-Conscious Users Worldwide</h2>
          <p className="mt-3 text-slate-600">See what our users are saying about their experience with {COMPANY.brand} Protection.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t) => (
            <figure key={t.name} className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm">
              <div className="flex items-center gap-1 text-amber-400">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
              </div>
              <blockquote className="mt-4 text-slate-700 leading-relaxed text-[15px]">
                &ldquo;{t.text}&rdquo;
              </blockquote>
              <figcaption className="mt-5 flex items-center gap-3">
                <img src={t.avatar} alt={t.name} className="h-11 w-11 rounded-full object-cover" />
                <div>
                  <p className="font-semibold text-slate-900">{t.name}</p>
                  <p className="text-xs text-slate-500">{t.role}</p>
                </div>
                <span className="ml-auto text-[10px] uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-1">Verified</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

export function PricingSection() {
  return (
    <section id="pricing" className="py-16 md:py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="section-title text-3xl md:text-4xl font-extrabold text-slate-900">Affordable Plans That Protect Your Phone</h2>
          <p className="mt-3 text-slate-600">Choose the plan that fits your needs. All plans include our 30-day money-back guarantee.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 gap-6">
          {PLANS.map((p) => (
            <div
              key={p.name}
              className={`relative rounded-2xl p-8 border ${p.popular ? "border-emerald-500 bg-emerald-50/40 shadow-xl" : "border-slate-200 bg-white shadow-sm"}`}
            >
              {p.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-[10px] uppercase tracking-widest px-3 py-1 rounded-full font-semibold">
                  Most Popular
                </span>
              )}
              <h3 className="text-xl font-bold text-slate-900">{p.name}</h3>
              <div className="mt-4 flex items-end gap-2">
                <span className="text-5xl font-extrabold text-slate-900">{p.price}</span>
              </div>
              <p className="text-sm text-slate-500">{p.period}</p>
              <ul className="mt-6 space-y-3">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-slate-700">
                    <Check className="h-4 w-4 mt-1 text-emerald-600" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button asChild className={`mt-7 w-full h-12 ${p.popular ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-900 hover:bg-slate-800"} text-white`}>
                <a href={`tel:${COMPANY.phoneRaw}`}>
                  <Phone className="h-4 w-4 mr-2" /> Call Now: {COMPANY.phone}
                </a>
              </Button>
            </div>
          ))}
        </div>
        <div className="mt-10 rounded-2xl bg-slate-900 text-white p-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-emerald-300 font-semibold">30-Day Money-Back Guarantee</p>
            <p className="mt-2 text-slate-200">Try {COMPANY.brand} risk-free. If you&apos;re not completely satisfied, we&apos;ll refund your purchase within 30 days. No questions asked.</p>
          </div>
          <Award className="h-12 w-12 text-emerald-400 shrink-0" />
        </div>
      </div>
    </section>
  );
}

export function FAQSection() {
  return (
    <section id="faq" className="py-16 md:py-24 bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="text-center">
          <h2 className="section-title text-3xl md:text-4xl font-extrabold text-slate-900">Frequently Asked Questions</h2>
          <p className="mt-3 text-slate-600">Everything you need to know about {COMPANY.brand} Protection.</p>
        </div>
        <Accordion type="single" collapsible className="mt-10 space-y-3">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="rounded-xl border border-slate-200 bg-white px-5">
              <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

export function TrustSection() {
  return (
    <section className="py-16 bg-white border-t border-slate-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
        <h2 className="section-title text-2xl md:text-3xl font-extrabold text-slate-900">Trusted by Security Experts</h2>
        <p className="mt-3 text-slate-600">Recognized for excellence in mobile security and privacy protection.</p>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {TRUST_BADGES.map((b) => (
            <div key={b} className="rounded-xl border border-slate-200 bg-slate-50 py-5 px-3">
              <p className="font-semibold text-slate-800">{b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
